<template>
  <!-- Offrir un abonnement (REDMINE-13728) : retrouver un compte par son email,
       son nom de profil ou son identifiant, lui offrir le premium, et la liste
       de tous les premiums offerts par l'equipe Aveine. Le retrait se fait sur
       la fiche de l'utilisateur. -->
  <div class="index py-5">
    <div class="d-flex align-center">
      <span class="d-flex flex-row page-title">{{ $t('intranet.title.menu.billing.grants') }}</span>
    </div>

    <v-card flat class="mt-4">
      <v-card-title>{{ $t('intranet.billing.grants.findUser') }}</v-card-title>
      <v-card-subtitle>{{ $t('intranet.billing.grants.findUserHint') }}</v-card-subtitle>
      <v-card-text>
        <v-form class="d-flex align-center" @submit.prevent="lookup()">
          <v-text-field v-model="query" :label="$t('intranet.billing.grants.query')" prepend-inner-icon="mdi-magnify"
                        clearable hide-details class="mr-4" />
          <v-btn depressed color="primary" type="submit" :loading="searching" :disabled="!query || query.trim().length < 2">
            {{ $t('intranet.billing.grants.search') }}
          </v-btn>
        </v-form>
        <v-data-table v-if="searched" dense hide-default-footer class="mt-4" :headers="userHeaders" :items="users"
                      :items-per-page="-1" :loading="searching" :no-data-text="$t('intranet.billing.grants.noUser')">
          <template v-slot:item.name="{ item }">
            <router-link :to="`/authenticated/users/read/${item.id}`">{{ item.name || item.id.slice(0, 8) }}</router-link>
          </template>
          <template v-slot:item.email="{ item }">{{ item.email || '—' }}</template>
          <template v-slot:item.actions="{ item }">
            <v-btn x-small depressed color="primary" @click="openOffer(item)">{{ $t('intranet.billing.grants.offer') }}</v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-card flat class="mt-6">
      <v-card-title>{{ $t('intranet.billing.grants.all') }}</v-card-title>
      <v-card-subtitle>{{ $t('intranet.billing.grants.intro') }}</v-card-subtitle>
      <v-card-text>
        <v-data-table :headers="grantHeaders" :items="grants" :loading="loading" :options.sync="options"
                      :server-items-length="total" :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
                      :no-data-text="$t('intranet.billing.grants.none')">
          <template v-slot:item.user="{ item }">
            <router-link :to="`/authenticated/users/read/${item['user-id']}`">{{ nameOf(item['user-id']) }}</router-link>
          </template>
          <template v-slot:item.state="{ item }">
            <v-chip x-small :color="state(item) === 'active' ? 'success' : 'grey lighten-1'">
              {{ $t(`intranet.billing.grants.states.${state(item)}`) }}
            </v-chip>
          </template>
          <template v-slot:item.starts-at="{ item }">{{ date(item['starts-at']) }}</template>
          <template v-slot:item.expires-at="{ item }">{{ item['expires-at'] ? date(item['expires-at']) : $t('intranet.billing.grants.noEnd') }}</template>
          <template v-slot:item.granted-by="{ item }">{{ nameOf(item['granted-by-id']) }}</template>
          <template v-slot:item.revoked-at="{ item }">
            {{ item['revoked-at'] ? `${date(item['revoked-at'])} (${nameOf(item['revoked-by-id'])})` : '—' }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <AveineGrantOfferDialog v-model="offerDialog" :user-id="offerUser && offerUser.id"
                            :user-label="offerUser && (offerUser.email || offerUser.name)" @offered="offered()" />
  </div>
</template>

<script>
import { errorHandler } from 'aveine-toolbox'
import { search, lookupUsers, grantState, profileName, formatDate } from '@/components/billing/billing-api'
import AveineGrantOfferDialog from '@/components/billing/AveineGrantOfferDialog.vue'

export default {
  name: 'BillingGrants',
  components: { AveineGrantOfferDialog },
  data () {
    return {
      query: '',
      users: [],
      searched: false,
      searching: false,
      grants: [],
      included: {},
      total: 0,
      loading: false,
      options: { page: 1, itemsPerPage: 25 },
      offerDialog: false,
      offerUser: null
    }
  },
  computed: {
    userHeaders () {
      return [
        { text: this.$t('intranet.billing.grants.name'), value: 'name', sortable: false },
        { text: this.$t('intranet.billing.grants.email'), value: 'email', sortable: false },
        { text: 'ID', value: 'id', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ]
    },
    grantHeaders () {
      return [
        { text: this.$t('intranet.billing.user'), value: 'user', sortable: false },
        { text: this.$t('intranet.billing.grants.state'), value: 'state', sortable: false },
        { text: this.$t('intranet.billing.grants.startsAt'), value: 'starts-at', sortable: false },
        { text: this.$t('intranet.billing.grants.expiresAt'), value: 'expires-at', sortable: false },
        { text: this.$t('intranet.billing.grants.reason'), value: 'reason', sortable: false },
        { text: this.$t('intranet.billing.grants.grantedBy'), value: 'granted-by', sortable: false },
        { text: this.$t('intranet.billing.grants.revokedAt'), value: 'revoked-at', sortable: false }
      ]
    }
  },
  watch: {
    options: { handler () { this.load() }, deep: true }
  },
  methods: {
    date (value) { return formatDate(value, this.$i18n.locale) },
    state (grant) { return grantState(grant) },
    nameOf (userId) { return profileName(this.included, userId) },
    async lookup () {
      this.searching = true
      try {
        this.users = await lookupUsers(this.$auth, this.query.trim())
        this.searched = true
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.searching = false
      }
    },
    openOffer (user) {
      this.offerUser = user
      this.offerDialog = true
    },
    offered () {
      this.options = { ...this.options, page: 1 }
    },
    async load () {
      this.loading = true
      try {
        const page = await search(this.$auth, '/billing-grants', {
          include: 'user.profile,granted_by.profile,revoked_by.profile',
          sort: '-starts-at',
          'page[number]': this.options.page,
          'page[size]': this.options.itemsPerPage
        })
        this.grants = page.items
        this.included = page.included
        this.total = page.total
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
